import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import BannerSlot from '../components/BannerSlot';
import AdCardCompact from '../components/ads/AdCardCompact';

interface Ad {
  id: number;
  title: string;
  price: number;
  price_type: string;
  quantity: number | null;
  weight: number | null;
  age_months: number | null;
  gender: string | null;
  city: { name_ar: string } | null;
  category: { id: number; name_ar: string; slug: string } | null;
  subcategory: { name_ar: string } | null;
  images: { image_url: string; thumbnail_url: string | null; is_primary: boolean }[];
  views_count: number;
  favorites_count: number;
  is_featured: boolean;
  is_promoted?: boolean;
  created_at: string;
  published_at: string;
  user: { id: number; name: string; avatar: string | null; is_verified: boolean } | null;
  tags: { id: number; name_ar: string }[];
}

interface Category { id: number; name_ar: string; slug: string; type: string; }
interface Region { id: number; name_ar: string; }
interface City { id: number; name_ar: string; }

const GENDERS = [{ v: 'male', l: 'ذكر' }, { v: 'female', l: 'أنثى' }, { v: 'mixed', l: 'مختلط' }];
const PRICE_TYPES = [{ v: 'negotiable', l: 'قابل للتفاوض' }, { v: 'fixed', l: 'سعر ثابت' }, { v: 'auction', l: 'مزاد' }];
const SORTS = [
  { v: 'latest', l: 'الأحدث' },
  { v: 'price_asc', l: 'الأقل سعراً' },
  { v: 'price_desc', l: 'الأعلى سعراً' },
  { v: 'popular', l: 'الأكثر مشاهدة' },
];

const emptyDraft = { regionId: '', cityId: '', priceMin: '', priceMax: '', gender: '', priceType: '' };

const AdsPage: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  // الفلاتر المطبّقة فعلياً
  const [categoryId, setCategoryId] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('latest');
  const [f, setF] = useState({ ...emptyDraft }); // region/city/price/gender/priceType المطبّقة

  // حالة الواجهة
  const [searchInput, setSearchInput] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [draft, setDraft] = useState({ ...emptyDraft });

  useEffect(() => {
    Promise.all([api.get('/categories'), api.get('/regions')])
      .then(([c, r]) => { setCategories(c.data.data || []); setRegions(r.data.data || []); })
      .catch(() => {});
  }, []);

  // مدن المسودة (داخل اللوحة)
  useEffect(() => {
    if (draft.regionId) {
      api.get('/regions/' + draft.regionId + '/cities').then(r => setCities(r.data.data || [])).catch(() => setCities([]));
    } else { setCities([]); }
  }, [draft.regionId]);

  const fetchAds = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page };
      if (categoryId) params.category_id = categoryId;
      if (q) params.q = q;
      if (f.regionId) params.region_id = f.regionId;
      if (f.cityId) params.city_id = f.cityId;
      if (f.priceMin) params.price_min = f.priceMin;
      if (f.priceMax) params.price_max = f.priceMax;
      if (f.gender) params.gender = f.gender;
      if (f.priceType) params.price_type = f.priceType;
      // الترتيب
      const map: Record<string, [string, string]> = {
        latest: ['created_at', 'desc'], price_asc: ['price', 'asc'],
        price_desc: ['price', 'desc'], popular: ['views_count', 'desc'],
      };
      const [sb, sd] = map[sort] || map.latest;
      params.sort_by = sb; params.sort_dir = sd;

      const res = await api.get('/ads', { params });
      const data = res.data.data;
      setAds(data?.ads || []);
      setLastPage(data?.pagination?.last_page || 1);
      setTotal(data?.pagination?.total || 0);
    } catch { setAds([]); }
    finally { setLoading(false); }
  }, [page, categoryId, q, f, sort]);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  const openFilter = () => { setDraft({ ...f }); setFilterOpen(true); };
  const applyFilter = () => { setF({ ...draft }); setPage(1); setFilterOpen(false); };
  const resetFilter = () => { setDraft({ ...emptyDraft }); setF({ ...emptyDraft }); setPage(1); setFilterOpen(false); };
  const submitSearch = (e: React.FormEvent) => { e.preventDefault(); setQ(searchInput.trim()); setPage(1); };

  const genderMap: Record<string, string> = { male: 'ذكر', female: 'أنثى', mixed: 'مختلط' };

  const activeCity = cities.find(c => String(c.id) === f.cityId)?.name_ar;
  const activeCatName = categories.find(c => String(c.id) === categoryId)?.name_ar;

  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (activeCatName) chips.push({ key: 'cat', label: activeCatName, clear: () => { setCategoryId(''); setPage(1); } });
  if (q) chips.push({ key: 'q', label: '"' + q + '"', clear: () => { setQ(''); setSearchInput(''); setPage(1); } });
  if (activeCity) chips.push({ key: 'city', label: activeCity, clear: () => { setF(x => ({ ...x, cityId: '' })); setPage(1); } });
  if (f.priceMin || f.priceMax) chips.push({ key: 'price', label: (f.priceMin || '0') + ' - ' + (f.priceMax || '∞') + ' ر.س', clear: () => { setF(x => ({ ...x, priceMin: '', priceMax: '' })); setPage(1); } });
  if (f.gender) chips.push({ key: 'gender', label: genderMap[f.gender], clear: () => { setF(x => ({ ...x, gender: '' })); setPage(1); } });
  if (f.priceType) chips.push({ key: 'pt', label: PRICE_TYPES.find(p => p.v === f.priceType)?.l || '', clear: () => { setF(x => ({ ...x, priceType: '' })); setPage(1); } });

  return (
    <>
      <style>{css}</style>
      <div className="mkt">
        {/* رقائق التصنيفات */}
        <div className="mkt-cats">
          <button className={'aj-chip' + (categoryId === '' ? ' is-active' : '')} onClick={() => { setCategoryId(''); setPage(1); }}>الكل</button>
          {categories.map(c => (
            <button key={c.id} className={'aj-chip' + (String(c.id) === categoryId ? ' is-active' : '')} onClick={() => { setCategoryId(String(c.id)); setPage(1); }}>{c.name_ar}</button>
          ))}
        </div>

        <BannerSlot position="market_top" />

        {/* شريط البحث + الفلتر */}
        <div className="mkt-search-row">
          <form className="mkt-search" onSubmit={submitSearch}>
            <span className="mkt-search-icon">🔍</span>
            <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="ابحث عن مواشي، خدمات..." />
            <button type="submit" className="aj-btn aj-btn-primary mkt-search-btn">بحث</button>
          </form>
          <button className="mkt-filter-btn" onClick={openFilter} title="الفلاتر">
            <span style={{ fontSize: 16 }}>⚙</span> الفلاتر
            {chips.length > 0 && <span className="mkt-filter-count">{chips.length}</span>}
          </button>
        </div>

        {/* رأس النتائج */}
        <div className="mkt-head">
          <div className="mkt-count">{total} إعلان{activeCity ? ' في ' + activeCity : ''}</div>
          <div className="mkt-head-right">
            {chips.length > 0 && (
              <div className="mkt-active-chips">
                {chips.map(ch => (
                  <span key={ch.key} className="mkt-active-chip">{ch.label}<button onClick={ch.clear}>✕</button></span>
                ))}
              </div>
            )}
            <select className="mkt-sort" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
              {SORTS.map(s => <option key={s.v} value={s.v}>ترتيب: {s.l}</option>)}
            </select>
          </div>
        </div>

        {/* القائمة */}
        {loading ? (
          <div className="mkt-empty">جاري التحميل...</div>
        ) : ads.length === 0 ? (
          <div className="mkt-empty"><div style={{ fontSize: 40 }}>🐑</div><p>لا توجد إعلانات مطابقة</p><small>جرّب تعديل الفلاتر أو البحث</small></div>
        ) : (
          <div className="ajc-list">
            {ads.map(ad => (
              <AdCardCompact key={ad.id} ad={ad} />
            ))}
          </div>
        )}

        {/* الترقيم */}
        {lastPage > 1 && (
          <div className="mkt-pager">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>» السابق</button>
            <span>صفحة {page} من {lastPage}</span>
            <button disabled={page >= lastPage} onClick={() => setPage(p => p + 1)}>التالي «</button>
          </div>
        )}
      </div>

      {/* لوحة الفلاتر (جانبية/Drawer) */}
      {filterOpen && <div className="mkt-overlay" onClick={() => setFilterOpen(false)} />}
      <aside className={'mkt-drawer' + (filterOpen ? ' open' : '')}>
        <div className="mkt-drawer-head">
          <span>الفلاتر</span>
          <button onClick={() => setFilterOpen(false)}>✕</button>
        </div>
        <div className="mkt-drawer-body">
          <div className="mkt-field">
            <label>المنطقة</label>
            <select value={draft.regionId} onChange={e => setDraft(d => ({ ...d, regionId: e.target.value, cityId: '' }))}>
              <option value="">كل المناطق</option>
              {regions.map(r => <option key={r.id} value={r.id}>{r.name_ar}</option>)}
            </select>
          </div>
          <div className="mkt-field">
            <label>المدينة</label>
            <select value={draft.cityId} onChange={e => setDraft(d => ({ ...d, cityId: e.target.value }))} disabled={!draft.regionId}>
              <option value="">{draft.regionId ? 'كل المدن' : 'اختر المنطقة أولاً'}</option>
              {cities.map(c => <option key={c.id} value={c.id}>{c.name_ar}</option>)}
            </select>
          </div>
          <div className="mkt-field">
            <label>نطاق السعر (ر.س)</label>
            <div className="mkt-price-range">
              <input type="number" min="0" placeholder="من" value={draft.priceMin} onChange={e => setDraft(d => ({ ...d, priceMin: e.target.value }))} />
              <span>—</span>
              <input type="number" min="0" placeholder="إلى" value={draft.priceMax} onChange={e => setDraft(d => ({ ...d, priceMax: e.target.value }))} />
            </div>
          </div>
          <div className="mkt-field">
            <label>الجنس</label>
            <div className="mkt-opts">
              {GENDERS.map(g => (
                <button key={g.v} className={'aj-chip' + (draft.gender === g.v ? ' is-active' : '')} onClick={() => setDraft(d => ({ ...d, gender: d.gender === g.v ? '' : g.v }))}>{g.l}</button>
              ))}
            </div>
          </div>
          <div className="mkt-field">
            <label>نوع السعر</label>
            <div className="mkt-opts">
              {PRICE_TYPES.map(p => (
                <button key={p.v} className={'aj-chip' + (draft.priceType === p.v ? ' is-active' : '')} onClick={() => setDraft(d => ({ ...d, priceType: d.priceType === p.v ? '' : p.v }))}>{p.l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="mkt-drawer-foot">
          <button className="aj-btn aj-btn-primary" style={{ flex: 1 }} onClick={applyFilter}>تطبيق الفلاتر</button>
          <button className="aj-btn aj-btn-outline" onClick={resetFilter}>إعادة تعيين</button>
        </div>
      </aside>
    </>
  );
};

const css = `
.mkt{max-width:960px;margin:0 auto;padding:16px 20px 40px;font-family:var(--aj-font)}
.mkt *{box-sizing:border-box}
.mkt-cats{display:flex;gap:8px;overflow-x:auto;padding:6px 0 10px;scrollbar-width:none}
.mkt-cats::-webkit-scrollbar{display:none}
.mkt-search-row{display:flex;gap:10px;align-items:center;margin:8px 0 14px}
.mkt-search{flex:1;display:flex;align-items:center;gap:8px;background:#fff;border:1.5px solid var(--aj-border);border-radius:var(--aj-radius);padding:6px 14px}
.mkt-search:focus-within{border-color:var(--aj-primary);box-shadow:0 0 0 3px rgba(17,94,84,.1)}
.mkt-search-icon{color:var(--aj-muted)}
.mkt-search input{flex:1;border:none;outline:none;font-family:var(--aj-font);font-size:14px;background:transparent;color:var(--aj-text)}
.mkt-search-btn{height:36px;padding:0 20px}
.mkt-filter-btn{display:inline-flex;align-items:center;gap:7px;height:50px;padding:0 18px;border-radius:var(--aj-radius);border:1.5px solid var(--aj-primary);background:var(--aj-primary-50);color:var(--aj-primary);font-family:var(--aj-font);font-weight:700;font-size:14px;cursor:pointer;position:relative;white-space:nowrap}
.mkt-filter-btn:hover{background:var(--aj-primary-100)}
.mkt-filter-count{background:var(--aj-accent);color:#fff;font-size:11px;font-weight:800;min-width:20px;height:20px;border-radius:9999px;display:inline-flex;align-items:center;justify-content:center;padding:0 5px}
.mkt-head{display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin-bottom:14px}
.mkt-count{font-size:15px;font-weight:800;color:var(--aj-text)}
.mkt-head-right{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.mkt-active-chips{display:flex;gap:6px;flex-wrap:wrap}
.mkt-active-chip{display:inline-flex;align-items:center;gap:5px;background:var(--aj-primary-50);color:var(--aj-primary);border:1px solid var(--aj-primary-100);font-size:12px;font-weight:700;padding:4px 10px;border-radius:9999px}
.mkt-active-chip button{border:none;background:none;color:var(--aj-primary);cursor:pointer;font-size:11px;padding:0;line-height:1}
.mkt-sort{appearance:none;padding:9px 32px 9px 14px;border:1.5px solid var(--aj-border);border-radius:var(--aj-radius-sm);font-family:var(--aj-font);font-size:13px;font-weight:600;color:var(--aj-text-2);background:#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23757575' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat left 12px center;cursor:pointer;outline:none}
.mkt-list{display:flex;flex-direction:column;gap:12px}
.mkt-card{display:flex;min-height:180px;background:#fff;border:1px solid var(--aj-border);border-radius:var(--aj-radius);overflow:hidden;text-decoration:none;color:inherit;transition:all .18s ease;box-shadow:var(--aj-shadow)}
.mkt-card:hover{box-shadow:var(--aj-shadow-md);transform:translateY(-2px)}
.mkt-card.is-feat{border:1.5px solid var(--aj-accent)}
.mkt-card-img{position:relative;width:190px;flex-shrink:0;background:linear-gradient(135deg,var(--aj-primary-50),var(--aj-primary-100));overflow:hidden}
.mkt-card-img img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.mkt-card-noimg{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:32px;color:var(--aj-primary)}
.mkt-feat{position:absolute;top:8px;right:8px}
.mkt-fav{position:absolute;top:8px;left:8px;width:32px;height:32px;border-radius:9999px;border:none;background:rgba(255,255,255,.92);color:#F43F5E;font-size:16px;cursor:pointer;display:flex;align-items:center;justify-content:center}
.mkt-views{position:absolute;bottom:8px;right:8px;background:rgba(0,0,0,.5);color:#fff;font-size:11px;padding:2px 8px;border-radius:9999px}
.mkt-card-body{flex:1;padding:14px 18px;display:flex;flex-direction:column;gap:8px;min-width:0}
.mkt-card-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.mkt-card-title{font-size:16px;font-weight:800;color:var(--aj-text);margin:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.mkt-verified{flex-shrink:0;background:#E8F5E9;color:#2E7D32;font-size:11px;font-weight:700;padding:3px 9px;border-radius:9999px}
.mkt-card-meta{display:flex;gap:6px;flex-wrap:wrap}
.mkt-tag{background:var(--aj-bg);border:1px solid var(--aj-border);color:var(--aj-text-2);font-size:11.5px;font-weight:600;padding:3px 9px;border-radius:9999px}
.mkt-card-sub{display:flex;gap:14px;flex-wrap:wrap;font-size:12px;color:var(--aj-muted)}
.mkt-card-bottom{display:flex;justify-content:space-between;align-items:flex-end;gap:10px;margin-top:auto}
.mkt-price{font-size:20px;font-weight:800;color:var(--aj-primary)}
.mkt-price small{font-size:12px;font-weight:600;color:var(--aj-text-2)}
.mkt-negotiable{display:block;font-size:11px;font-weight:600;color:var(--aj-accent);margin-top:2px}
.mkt-actions{display:flex;gap:8px;flex-shrink:0}
.mkt-act{height:36px;padding:0 14px;font-size:12.5px}
.mkt-empty{background:#fff;border:1px solid var(--aj-border);border-radius:var(--aj-radius);padding:60px 20px;text-align:center;color:var(--aj-muted)}
.mkt-empty p{font-size:15px;color:var(--aj-text-2);margin:10px 0 4px;font-weight:700}
.mkt-pager{display:flex;justify-content:center;align-items:center;gap:16px;margin-top:24px}
.mkt-pager button{background:#fff;border:1.5px solid var(--aj-border);border-radius:var(--aj-radius-sm);padding:8px 18px;font-family:var(--aj-font);font-weight:700;font-size:13px;color:var(--aj-primary);cursor:pointer}
.mkt-pager button:disabled{color:var(--aj-muted);cursor:not-allowed;opacity:.6}
.mkt-pager span{font-size:13px;color:var(--aj-text-2);font-weight:600}
/* Drawer */
.mkt-overlay{position:fixed;inset:0;background:rgba(0,0,0,.4);z-index:2500;animation:mktfade .2s ease}
@keyframes mktfade{from{opacity:0}to{opacity:1}}
.mkt-drawer{position:fixed;top:0;right:0;height:100vh;width:360px;max-width:90vw;background:#fff;z-index:2600;box-shadow:var(--aj-shadow-lg);display:flex;flex-direction:column;transform:translateX(100%);transition:transform .25s ease;font-family:var(--aj-font)}
.mkt-drawer.open{transform:translateX(0)}
.mkt-drawer-head{display:flex;justify-content:space-between;align-items:center;padding:18px 20px;border-bottom:1px solid var(--aj-border);font-size:17px;font-weight:800;color:var(--aj-text)}
.mkt-drawer-head button{border:none;background:none;font-size:18px;cursor:pointer;color:var(--aj-muted)}
.mkt-drawer-body{flex:1;overflow-y:auto;padding:18px 20px;display:flex;flex-direction:column;gap:18px}
.mkt-field label{display:block;font-size:13px;font-weight:700;color:var(--aj-text-2);margin-bottom:8px}
.mkt-field select,.mkt-price-range input{width:100%;height:44px;padding:0 12px;border:1.5px solid var(--aj-border);border-radius:var(--aj-radius-sm);font-family:var(--aj-font);font-size:14px;outline:none;background:#fff;color:var(--aj-text)}
.mkt-field select:focus,.mkt-price-range input:focus{border-color:var(--aj-primary)}
.mkt-price-range{display:flex;align-items:center;gap:8px}
.mkt-price-range span{color:var(--aj-muted)}
.mkt-opts{display:flex;gap:8px;flex-wrap:wrap}
.mkt-drawer-foot{display:flex;gap:10px;padding:16px 20px;border-top:1px solid var(--aj-border)}
@media(max-width:640px){
  .mkt-card{flex-direction:column}
  .mkt-card-img{width:100%;height:190px}
  .mkt-card-bottom{flex-direction:column;align-items:stretch}
  .mkt-actions{width:100%}
  .mkt-act{flex:1}
  .mkt-drawer{width:100%;max-width:100%}
}
`;

export default AdsPage;
